import { Box, Button, Grid, Popover, PopoverContent, PopoverTrigger, Text, Tooltip, useDisclosure } from '@chakra-ui/core';
import { TFunction } from 'i18next';
import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AiTwotoneExperiment } from 'react-icons/ai';
import { BsFillGridFill } from 'react-icons/bs';
import { IoMdAnalytics } from 'react-icons/io';
import { MdAnalytics, MdPersonSearch } from 'react-icons/md';
import { Link as RouterLink, useRouteMatch } from 'react-router-dom';
import { PATH_ANALYTICS, PATH_CANDIDATE_SEARCH, PATH_MANAGEMENT_OVERVIEW, PATH_PROTOTYPE } from '../../routes/constants';
import colors from '../../styles/colors';

interface HeaderMenuWithPopoverProps {
  showPrototype: boolean;
  showManagementOverviewIcon: boolean;
  showAnalytics: boolean;
  isCurrentPage?: (path: string) => boolean;
}

const ICON_SIZE = 32;
const ITEM_SIZE = '88px';

const LinkWithRef = forwardRef<HTMLAnchorElement, React.ComponentProps<typeof RouterLink>>((props, ref) => (
  <RouterLink ref={ref} {...props} />
));
LinkWithRef.displayName = 'LinkWithRef';

const getFilteredGridItems = (
  t: TFunction,
  showPrototype: boolean,
  showAnalytics: boolean,
  showManagementOverviewIcon: boolean,
) => {
  const gridItems = [
    showAnalytics && {
      icon: IoMdAnalytics,
      label: t('menu.analytics'),
      color: colors.gray[450],
      link: PATH_ANALYTICS,
      id: 'analytics',
    },
    showPrototype && {
      icon: AiTwotoneExperiment,
      label: t('menu.prototype'),
      color: '#38A169',
      link: PATH_PROTOTYPE,
      id: 'prototype',
    },
    {
      icon: MdPersonSearch,
      label: t('menu.candidateSearch'),
      color: colors.persianBlue,
      link: PATH_CANDIDATE_SEARCH,
      id: 'candidateSearch',
    },
    showManagementOverviewIcon && {
      icon: MdAnalytics,
      label: t('menu.managementOverview'),
      color: '#DD6B20',
      link: PATH_MANAGEMENT_OVERVIEW,
      id: 'managementOverview',
    },
  ];

  return gridItems.filter(Boolean) as {
    icon: React.ElementType;
    label: string;
    color: string;
    link: string;
    id: string;
  }[];
};

const HeaderMenuModal: React.FC<HeaderMenuWithPopoverProps> = ({ showPrototype, showManagementOverviewIcon, showAnalytics }) => {
  const { t } = useTranslation('headerMenu');
  const match = useRouteMatch();
  const gridItems = getFilteredGridItems(t, showPrototype, showAnalytics, showManagementOverviewIcon);
  const isCurrentPage = (path: string): boolean => !!match?.url?.includes(path);

  return (
    <Box bg="white" borderRadius="30px" p={6} data-testid="header-menu-modal" borderWidth="8px" borderColor={colors.gray[250]}>
      <Grid templateColumns={`repeat(2, ${ITEM_SIZE})`} justifyContent="center" gap={6}>
        {gridItems.map(({ icon: Icon, label, color, link, id }) => {
          const isSelected = isCurrentPage(link);
          return (
            <Box
              key={`menu-item-${id}`}
              as={isSelected ? /* istanbul ignore next */ 'div' : LinkWithRef}
              {...(!isSelected ? { to: link } : /* istanbul ignore next */ {})}
              role="group"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              borderRadius="xl"
              w={ITEM_SIZE}
              h={ITEM_SIZE}
              data-testid={`menu-item-${id}`}
              transition="all 0.2s"
              cursor={isSelected ? /* istanbul ignore next */ 'default' : 'pointer'}
              bg={isSelected ? /* istanbul ignore next */ colors.gray[50] : 'transparent'}
              _hover={isSelected ? /* istanbul ignore next */ {} : { bg: colors.gray[50] }}
            >
              <Icon size={ICON_SIZE} color={color} />
              <Text fontSize="xs" mt={2} fontWeight="medium" color="gray.700" whiteSpace="normal">
                {label}
              </Text>
            </Box>
          );
        })}
      </Grid>
    </Box>
  );
};

const HeaderMenuWithPopover: React.FC<HeaderMenuWithPopoverProps> = ({
  showPrototype,
  showAnalytics,
  showManagementOverviewIcon,
}) => {
  const { t } = useTranslation('headerMenu');
  const { isOpen, onToggle, onClose } = useDisclosure();
  const match = useRouteMatch();
  const isCurrentPage = (path: string): boolean => !!match?.url?.includes(path);

  const gridItems = getFilteredGridItems(t, showPrototype, showAnalytics, showManagementOverviewIcon);

  if (gridItems.length === 1) {
    const { icon: Icon, label, link, id } = gridItems[0];
    const isSelected = isCurrentPage(link);
    return (
      <Tooltip label={label}>
        <Box
          key={`menu-item-${id}`}
          as={LinkWithRef}
          to={link}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="30px"
          px={4}
          py={1}
          cursor="pointer"
          _hover={{ bg: 'whiteAlpha.300' }}
          data-testid={`single-menu-item-${id}`}
          bg={isSelected ? /* istanbul ignore next */ 'whiteAlpha.300' : 'transparent'}
        >
          <Icon size={ICON_SIZE} color="#fff" />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Popover placement="bottom" isOpen={isOpen} onClose={onClose} closeOnBlur>
      <PopoverTrigger>
        <Button
          variant="ghost"
          aria-label="Open Header Menu"
          data-testid="header-menu-button"
          onClick={onToggle}
          borderRadius="30px"
          px={4}
          py={1}
          bg={isOpen ? 'whiteAlpha.300' : 'transparent'}
          _focus={{ boxShadow: 'none' }}
          _hover={{ outline: 'none', boxShadow: 'none', bg: 'whiteAlpha.300' }}
        >
          <Tooltip label="Menu" isDisabled={isOpen}>
            <Box>
              <BsFillGridFill size={ICON_SIZE} color="#fff" />
            </Box>
          </Tooltip>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        borderRadius="30px"
        borderColor={colors.gray[250]}
        boxShadow="lg"
        data-testid="popover-content"
        _focus={{ outline: 'none', boxShadow: 'none' }}
        unmountOnExit
        zIndex={10}
      >
        <HeaderMenuModal
          showPrototype={showPrototype}
          showManagementOverviewIcon={showManagementOverviewIcon}
          showAnalytics={showAnalytics}
          isCurrentPage={isCurrentPage}
        />
      </PopoverContent>
    </Popover>
  );
};

export default HeaderMenuWithPopover;
